import { useEffect, useState } from "react";
import { isPostVersionReloadNavigation } from "../utils/reloadForNewVersion";

type Options = {
  currentVersion: string | undefined;
  url?: string;
};

type VersionPayload = { version?: string };

const SW_UPDATE_WAIT_MS = 2500;

async function nudgeServiceWorkerUpdate(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  try {
    await registration.update();
  } catch {
    // ignore — update() can throw if the SW script is unreachable
  }

  const installing = registration.installing;
  if (!installing) return;

  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, SW_UPDATE_WAIT_MS);
    const onStateChange = (): void => {
      if (installing.state === "installed" || installing.state === "activated") {
        installing.removeEventListener("statechange", onStateChange);
        window.clearTimeout(timeout);
        resolve();
      }
    };
    installing.addEventListener("statechange", onStateChange);
  });
}

/**
 * Polls a static `version.json` file on window focus and online events to
 * detect when a new version has been deployed. The file is generated at
 * build time by `versionFilePlugin` and must be served with `no-store`
 * caching so this check sees fresh content.
 */
export function useVersionFileCheck({ currentVersion, url = "/version.json" }: Options): boolean {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!currentVersion || currentVersion === "dev") return;
    if (stale) return;

    let cancelled = false;
    let inFlight = false;
    let lastCheckAt = 0;
    const MIN_INTERVAL_MS = 2000;

    const check = async (): Promise<void> => {
      if (inFlight) return;
      if (isPostVersionReloadNavigation()) return;
      const now = Date.now();
      if (now - lastCheckAt < MIN_INTERVAL_MS) return;
      inFlight = true;
      lastCheckAt = now;
      try {
        const res = await fetch(`${url}?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as VersionPayload;
        if (cancelled) return;
        if (data.version && data.version !== currentVersion) {
          void nudgeServiceWorkerUpdate();
          if (cancelled) return;
          setStale(true);
        }
      } catch {
        // Network errors are ignored; we'll try again on next focus/online.
      } finally {
        inFlight = false;
      }
    };

    void check();

    const onFocus = (): void => {
      void check();
    };
    const onOnline = (): void => {
      void check();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void check();
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentVersion, url, stale]);

  return stale;
}
